import React, { useState } from 'react';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';

export interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Title row — string or any ReactNode (chips, badges, etc.). */
  title?: React.ReactNode;
  /** Extra action buttons rendered before the maximize/close controls. */
  actions?: React.ReactNode;
  /** Pinned footer row (navigation buttons, save/cancel, etc.). */
  footer?: React.ReactNode;
  /** Panel body. */
  children: React.ReactNode;
  /** When true, backdrop clicks and ESC are suppressed (unsaved-edit guard). */
  disableClose?: boolean;
  /** Default width as a CSS value. Defaults to `min(92vw, 1440px)`. */
  defaultWidth?: string;
}

/**
 * Generic right-anchored side drawer with a consistent header
 * (title · actions · maximize · close) and optional footer.
 *
 * Used by: SchemaDetailDrawer, DataPreviewModal, NewTransformationWizard.
 */
const SideDrawer: React.FC<SideDrawerProps> = ({
  open,
  onClose,
  title,
  actions,
  footer,
  children,
  disableClose,
  defaultWidth = 'min(92vw, 1440px)',
}) => {
  const [maximized, setMaximized] = useState(false);

  const handleClose = (_e: object, _reason: 'backdropClick' | 'escapeKeyDown') => {
    if (disableClose) return;
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: {
          width: maximized ? '100vw' : defaultWidth,
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {typeof title === 'string' ? (
              <Typography variant="h6" fontWeight={600} noWrap>
                {title}
              </Typography>
            ) : (
              title
            )}
          </Box>
          {actions}
          <IconButton
            size="small"
            onClick={() => setMaximized((v) => !v)}
            title={maximized ? 'Restore' : 'Maximize'}
          >
            {maximized ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton size="small" onClick={onClose} disabled={disableClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Body */}
      <Box
        sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {children}
      </Box>

      {/* Footer */}
      {footer && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          {footer}
        </Box>
      )}
    </Drawer>
  );
};

export default SideDrawer;
